import { useState } from "react";

import { RoleGate } from "@/components/RoleGate";
import {
  downloadRelatorioBspo,
  downloadRelatorioContagem,
  downloadRelatorioDispensacaoAltoCusto,
  downloadRelatorioLivro,
  useUnidades,
  type FormatoRelatorio,
} from "@/hooks/useApi";

function isoHoje(): string {
  return new Date().toISOString().slice(0, 10);
}
function isoInicioMes(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

const MESES = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

const agora = new Date();

export function RelatoriosPage() {
  return (
    <RoleGate allowed={["admin", "auditor", "farmaceutico"]}>
      <Inner />
    </RoleGate>
  );
}

function Inner() {
  const { data: unidades } = useUnidades();
  const semCnpj = (unidades ?? []).filter((u) => u.ativo && !u.cnpj);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-2xl font-semibold">Relatórios</h2>
        <p className="text-sm text-ink-muted">
          Documentos por <b>estabelecimento</b> (a escrituração da Port. 344 é por CNPJ).
          Baixe em PDF pra impressão/assinatura da RT, ou CSV pra Excel.
        </p>
        {semCnpj.length > 0 ? (
          <p className="mt-1 text-xs text-amber-700">
            ⚠️ Unidade(s) sem CNPJ cadastrado: {semCnpj.map((u) => u.nome).join(", ")} — informe
            em Admin → Unidades pro cabeçalho do livro sair completo.
          </p>
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <CardLivro />
        <CardBspo />
        <CardContagem />
        <CardDispensacaoAltoCusto />
      </div>
    </div>
  );
}

function CardDispensacaoAltoCusto() {
  const [inicio, setInicio] = useState(isoInicioMes());
  const [fim, setFim] = useState(isoHoje());
  const [unidade, setUnidade] = useState("");
  const [formato, setFormato] = useState<FormatoRelatorio>("pdf");
  const { pending, erro, run } = useDownload();

  return (
    <div className="card p-4 space-y-3">
      <div>
        <h3 className="font-display text-base font-semibold">Dispensação de Alto Custo</h3>
        <p className="text-xs text-ink-muted">
          Dispensações de <b>alto custo e controlados</b> no período, com <b>nome do médico
          prescritor</b> e CRM. Anuladas aparecem marcadas (rastreabilidade ANVISA).
        </p>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs uppercase tracking-wider text-ink-muted">Início</label>
          <input className="input mt-1" type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-ink-muted">Fim</label>
          <input className="input mt-1" type="date" value={fim} onChange={(e) => setFim(e.target.value)} />
        </div>
      </div>
      <SeletorUnidade value={unidade} onChange={setUnidade} opcional />
      <SeletorFormato value={formato} onChange={setFormato} />
      <button
        className="btn-primary w-full"
        disabled={pending || !inicio || !fim}
        onClick={() => run(() => downloadRelatorioDispensacaoAltoCusto({
          inicio, fim, unidade_id: unidade || null, formato,
        }))}
      >
        {pending ? "Gerando…" : "⬇ Baixar dispensações"}
      </button>
      {erro ? <p className="text-xs text-anarosa">{erro}</p> : null}
    </div>
  );
}

function useDownload() {
  const [pending, setPending] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  async function run(fn: () => Promise<void>) {
    setPending(true);
    setErro(null);
    try {
      await fn();
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setPending(false);
    }
  }
  return { pending, erro, run };
}

function SeletorFormato({ value, onChange }: { value: FormatoRelatorio; onChange: (f: FormatoRelatorio) => void }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-ink-muted">Formato</label>
      <select className="input mt-1" value={value} onChange={(e) => onChange(e.target.value as FormatoRelatorio)}>
        <option value="pdf">PDF (impressão / assinatura)</option>
        <option value="csv">CSV (Excel)</option>
      </select>
    </div>
  );
}

function SeletorUnidade({ value, onChange, opcional }: {
  value: string; onChange: (v: string) => void; opcional?: boolean;
}) {
  const { data: unidades } = useUnidades();
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-ink-muted">Estabelecimento</label>
      <select className="input mt-1" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">{opcional ? "Todas as unidades (consolidado)" : "— escolha a unidade —"}</option>
        {(unidades ?? []).filter((u) => u.ativo).map((u) => (
          <option key={u.id} value={u.id}>{u.nome}{u.cnpj ? ` — ${u.cnpj}` : ""}</option>
        ))}
      </select>
    </div>
  );
}

function CardLivro() {
  const [unidade, setUnidade] = useState("");
  const [ano, setAno] = useState(agora.getFullYear());
  const [mes, setMes] = useState(agora.getMonth() + 1);
  const [formato, setFormato] = useState<FormatoRelatorio>("pdf");
  const { pending, erro, run } = useDownload();

  return (
    <div className="card p-4 space-y-3">
      <div>
        <h3 className="font-display text-base font-semibold">Livro de Controlados</h3>
        <p className="text-xs text-ink-muted">
          Espelho mensal da escrituração (Port. 344/98): saldo anterior, movimentos e saldo final por substância.
        </p>
      </div>
      <SeletorUnidade value={unidade} onChange={setUnidade} />
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs uppercase tracking-wider text-ink-muted">Mês</label>
          <select className="input mt-1" value={mes} onChange={(e) => setMes(Number(e.target.value))}>
            {MESES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-ink-muted">Ano</label>
          <input className="input mt-1" type="number" min={2024} max={2100} value={ano}
                 onChange={(e) => setAno(Number(e.target.value))} />
        </div>
      </div>
      <SeletorFormato value={formato} onChange={setFormato} />
      <button
        className="btn-primary w-full"
        disabled={!unidade || pending}
        onClick={() => run(() => downloadRelatorioLivro({ unidade_id: unidade, ano, mes, formato }))}
      >
        {pending ? "Gerando…" : "⬇ Baixar livro"}
      </button>
      {erro ? <p className="text-xs text-anarosa">{erro}</p> : null}
    </div>
  );
}

function CardBspo() {
  const [unidade, setUnidade] = useState("");
  const [ano, setAno] = useState(agora.getFullYear());
  const [trimestre, setTrimestre] = useState<string>("");
  const [formato, setFormato] = useState<FormatoRelatorio>("pdf");
  const { pending, erro, run } = useDownload();

  return (
    <div className="card p-4 space-y-3">
      <div>
        <h3 className="font-display text-base font-semibold">BSPO</h3>
        <p className="text-xs text-ink-muted">
          Balanço de Substâncias Psicoativas: entradas, saídas e perdas no período.
          Confirme o modelo exigido com a Vigilância Sanitária municipal.
        </p>
      </div>
      <SeletorUnidade value={unidade} onChange={setUnidade} />
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs uppercase tracking-wider text-ink-muted">Período</label>
          <select className="input mt-1" value={trimestre} onChange={(e) => setTrimestre(e.target.value)}>
            <option value="">Ano inteiro</option>
            <option value="1">1º trimestre</option>
            <option value="2">2º trimestre</option>
            <option value="3">3º trimestre</option>
            <option value="4">4º trimestre</option>
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-ink-muted">Ano</label>
          <input className="input mt-1" type="number" min={2024} max={2100} value={ano}
                 onChange={(e) => setAno(Number(e.target.value))} />
        </div>
      </div>
      <SeletorFormato value={formato} onChange={setFormato} />
      <button
        className="btn-primary w-full"
        disabled={!unidade || pending}
        onClick={() => run(() => downloadRelatorioBspo({
          unidade_id: unidade, ano, trimestre: trimestre ? Number(trimestre) : null, formato,
        }))}
      >
        {pending ? "Gerando…" : "⬇ Baixar BSPO"}
      </button>
      {erro ? <p className="text-xs text-anarosa">{erro}</p> : null}
    </div>
  );
}

function CardContagem() {
  const [unidade, setUnidade] = useState("");
  const [formato, setFormato] = useState<FormatoRelatorio>("pdf");
  const { pending, erro, run } = useDownload();

  return (
    <div className="card p-4 space-y-3">
      <div>
        <h3 className="font-display text-base font-semibold">Contagem de Controlados</h3>
        <p className="text-xs text-ink-muted">
          Foto de hoje: físico por local (armário/caixas/enfermagem/farmácia) × saldo do livro,
          com divergências. Base da contagem mensal.
        </p>
      </div>
      <SeletorUnidade value={unidade} onChange={setUnidade} opcional />
      <SeletorFormato value={formato} onChange={setFormato} />
      <button
        className="btn-primary w-full"
        disabled={pending}
        onClick={() => run(() => downloadRelatorioContagem({ unidade_id: unidade || null, formato }))}
      >
        {pending ? "Gerando…" : "⬇ Baixar contagem"}
      </button>
      {erro ? <p className="text-xs text-anarosa">{erro}</p> : null}
    </div>
  );
}
